import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import React, { useContext, useState } from "react";
import { useLocation } from "react-router";
import LoadingIcon from "src/Components/Common/LoadingIcon";
import useFetch from "src/Components/Common/useFetch";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
import { LoggedInSkeleton } from "../../Common/LoggedInSkeleton";
import { getReconTopBarButtons } from "../../Common/TopNavBar";
import { userContext } from "../../Contexts/userContext";

const LedgerUpload = () => {
  const location = useLocation<any>();
  const { actor } = useContext(userContext);
  const [fileType, setFileType] = useState("TVLR");
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [showLoadingIcon, setShowLoadingIcon] = useState(false);
  const [error, setError] = useState("");
  const [syncResults, setSyncResults] = useState<{
    success: boolean;
    successCount: number;
    failureCount: number;
    fileResults: Array<{
      fileName: string;
      fileErrors: Array<string>;
      validationErrors: Array<{
        type: string; // "validation"
        message: string;
        index?: number; // Optional, if applicable
        count?: number; // Optional, if applicable
        body?: string; // Optional, if applicable
      }>;
      dbErrors: Array<{
        type: string; // "db"
        message: string;
        count: number; // Number of affected rows
        index?: number; // Optional, if applicable
      }>;
      isSuccess: boolean;
      totalRows: number;
      processedRows: number;
    }>;
  } | null>(null);

  // Function to parse sync response and extract meaningful information
  const handleSyncResponse = (responseData: any) => {
    if (!responseData || !responseData.data) return null;

    const { successCount, failureCount, processedFiles } = responseData.data;

    // Consider the operation successful if the API returns "Success",
    // even if some files failed to process
    const isSuccess = responseData.response === "Success";

    const fileResults = processedFiles.map((file) => {
      const fileName = file.originalName;
      const isSuccess = file.success;
      const totalRows = file.orginalTotalRows || 0;
      const processedRows = file.success && file.uploadLedgerResponse ? file.uploadLedgerResponse.processedRecords : 0;

      // Extract validation and DB errors from successful files
      const fileErrors = [];
      let validationErrors = [];
      let dbErrors = [];
      // For failed files, format the error message
      if (file.error) {
        let errorMsg = file.error || "";

        // Extract the meaningful part of the error
        if (errorMsg) {
          if (errorMsg.includes("original_name_")) {
            // If the error message contains "original_name_", extract the part after it
            errorMsg = errorMsg.split("original_name_")[1].trim();
          }
          errorMsg = errorMsg.trim();
        }
        fileErrors.push(errorMsg);
      }

      // Process validation errors from successful files
      if (file.uploadLedgerResponse && file.uploadLedgerResponse.validationError) {
        const validationErrorData = file.uploadLedgerResponse.validationError;
        if (validationErrorData.count > 0 && validationErrorData.errors && validationErrorData.errors.length > 0) {
          validationErrors = validationErrorData.errors.map((err) => ({
            type: "validation",
            message: err.error || "Validation error",
            // index: err.index !== undefined ? err.index : -1,
            body: JSON.stringify(err.body) || "",
          }));
        }
      }

      // Process DB errors from successful files
      if (file.uploadLedgerResponse && file.uploadLedgerResponse.dbError) {
        const dbErrorData = file.uploadLedgerResponse.dbError;
        if (dbErrorData.count > 0 && dbErrorData.errors && dbErrorData.errors.length > 0) {
          dbErrors = dbErrorData.errors.map((err) => ({
            type: "db",
            message: err.error || "Database error",
            count: err.count || 1,
            // index: err.index !== undefined ? err.index : -1,
          }));
        }
      }
      // make set for validationErrors and dbErrors to avoid duplicates and make it array of objects
      const validationErrorSet = new Set(validationErrors.map((err) => JSON.stringify(err)));
      validationErrors = Array.from(validationErrorSet).map((err) => JSON.parse(err));
      const dbErrorSet = new Set(dbErrors.map((err) => JSON.stringify(err)));
      dbErrors = Array.from(dbErrorSet).map((err) => JSON.parse(err));

      return {
        fileName,
        isSuccess,
        totalRows,
        processedRows,
        fileErrors,
        validationErrors,
        dbErrors,
      };
    });

    return {
      success: isSuccess,
      successCount,
      failureCount,
      fileResults,
    };
  };

  const syncLedgerData = () => {
    setSyncResults(null);
    setShowLoadingIcon(true);

    setTimeout(() => {
      console.log("Calling sync API...");

      useFetch(API_ENDPOINTS.INTEGRATION_LEDGER_DATA_UPLOAD_SYNC.url, "GET", {
        failureMessage: "Failed to sync ledger data. Please try again later.",
        showSuccessToast: true,
        thenCallBack: (response) => {
          console.log("Sync API response:", response); // Log the complete response for debugging

          if (response && response.data && response.data.response === "Success") {
            // Process the sync response
            const results = handleSyncResponse(response.data);
            setSyncResults(results);
          } else {
            console.error("Unexpected response format:", response);
            setError("Could not process sync response. Please contact support.");
          }
          setShowLoadingIcon(false);
        },
        catchCallBack: (error) => {
          console.error("Sync API error after upload:", error);
          setError("Files uploaded successfully, but sync operation failed. Please try syncing again.");
          setShowLoadingIcon(false);
        },
      });
    }, 2000); // Simulate loading icon for 2 second
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    // Check file count
    if (files.length > 10) {
      setError("You can only upload a maximum of 10 files at a time");
      return;
    }

    // Check file types - allow only .xlsx, xls, xlsb and .csv
    const invalidFiles = Array.from(files).filter(
      (file) =>
        !file.name.endsWith(".xlsx") &&
        !file.name.endsWith(".csv") &&
        !file.name.endsWith(".xls") &&
        !file.name.endsWith(".xlsb")
    );

    if (invalidFiles.length > 0) {
      setError("Only .xlsx and .csv files are allowed");
      return;
    }

    setError("");
    setSelectedFiles(Array.from(files));
  };

  const uploadAndSyncLedgerData = async () => {
    // Reset previous results
    setSyncResults(null);

    if (selectedFiles.length === 0) {
      setError("Please select at least one file to upload");
      return;
    }

    setShowLoadingIcon(true);
    setError("");

    const formData = new FormData();
    formData.append("fileType", fileType);
    selectedFiles.forEach((file) => {
      formData.append("files", file);
    });

    console.log("Calling upload API...");

    useFetch(API_ENDPOINTS.INTEGRATION_LEDGER_DATA_UPLOAD.url, "POST", {
      failureMessage: API_ENDPOINTS.INTEGRATION_LEDGER_DATA_UPLOAD.failureMessage,
      showSuccessToast: true,
      data: formData,
      config: {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
      thenCallBack: (uploadResponse) => {
        console.log("Upload API response:", uploadResponse);
        // After successful upload, call the sync API
        // Ensure the upload API was successful before proceeding
        if (uploadResponse && uploadResponse.data && uploadResponse.data.response === "Success") {
          console.log("Upload successful, now calling sync API...");

          // Reset form
          setSelectedFiles([]);
          // setFileType("TVLR");

          // Reset file input element to allow re-upload of the same file
          // Find file input element and reset its value
          const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
          if (fileInput) {
            fileInput.value = "";
          }
          syncLedgerData();
        } else {
          console.error("Upload API returned unsuccessful response:", uploadResponse);
          setError("Failed to upload files. Please try again.");
          setShowLoadingIcon(false);
        }
      },
      catchCallBack: () => {
        console.error("Upload API error:", error);
        setShowLoadingIcon(false);
        setError("Failed to upload files. Please try again.");
      },
    });
  };

  return (
    <LoggedInSkeleton
      topBarButtons={getReconTopBarButtons("Ledger Analytics", actor.name, location?.state?.openCollapseObj, actor)}
    >
      <Grid>
        <Grid>
          <Card className="mt_20">
            <CardContent>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" className="fw_600">
                  Upload Ledger Data
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={syncLedgerData}
                  disabled={showLoadingIcon}
                  sx={{
                    minWidth: "120px",
                    padding: "8px 16px",
                    fontSize: { xs: "14px", sm: "14px", md: "16px" },
                    marginLeft: "auto", // This pushes the button to the right
                    alignSelf: "center", // This ensures vertical alignment with the text
                  }}
                >
                  <LoadingIcon loading={showLoadingIcon && selectedFiles.length === 0} />
                  Sync Data
                </Button>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="file-type-label">File Type</InputLabel>
                    <Select
                      labelId="file-type-label"
                      id="file-type-select"
                      value={fileType}
                      label="File Type"
                      onChange={(e) => setFileType(e.target.value)}
                    >
                      <MenuItem value="TVLR">TVLR</MenuItem>
                      <MenuItem value="TVHR">TVHR</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Button variant="outlined" component="label" fullWidth style={{ height: "40px" }}>
                    Select Files (.xlsx, .xls, .xlsb, .csv)
                    <input type="file" multiple accept=".xlsx, .xls, .xlsb, .csv" hidden onChange={handleFileChange} />
                  </Button>
                </Grid>

                {selectedFiles.length > 0 && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" className="fw_600" gutterBottom>
                      Selected Files ({selectedFiles.length})
                    </Typography>
                    <ul style={{ maxHeight: "200px", overflowY: "auto", padding: "0 16px" }}>
                      {selectedFiles.map((file, index) => (
                        <li key={index}>{file.name}</li>
                      ))}
                    </ul>
                  </Grid>
                )}

                {error && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="error">
                      {error}
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12} sx={{ mt: 2, textAlign: "center" }}>
                  <Button
                    className="theme_btn"
                    onClick={uploadAndSyncLedgerData}
                    disabled={selectedFiles.length === 0 || showLoadingIcon}
                    variant="contained"
                    sx={{
                      minWidth: "140px",
                      padding: "10px 24px",
                      fontSize: { xs: "16px", sm: "16px", md: "18px" },
                      "@media (max-width: 600px)": {
                        padding: "8px 16px",
                      },
                    }}
                  >
                    <LoadingIcon loading={showLoadingIcon} />
                    Upload & Sync Data
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          {/* Display sync results if available */}
          {syncResults && (
            <Card className="mt_20">
              <CardContent>
                <Typography variant="h6" className="fw_600" gutterBottom>
                  Sync Results
                </Typography>

                <Box sx={{ mt: 2 }}>
                  <Alert severity={syncResults.failureCount > 0 ? "info" : "success"} sx={{ mb: 2 }}>
                    {syncResults.successCount === 0 && syncResults.failureCount === 0
                      ? "No files were processed."
                      : syncResults.failureCount === 0
                      ? `Out of ${
                          syncResults.successCount + syncResults.failureCount
                        } files, we successfully processed ${syncResults.successCount} files.`
                      : syncResults.successCount > 0
                      ? `Out of ${
                          syncResults.successCount + syncResults.failureCount
                        } files, we successfully processed ${syncResults.successCount} files and ${
                          syncResults.failureCount
                        } files had issues.`
                      : `Out of ${syncResults.failureCount} files, none were successfully processed.`}
                  </Alert>

                  {/* Display successful file details */}
                  {syncResults.fileResults && syncResults.fileResults.filter((file) => file.isSuccess).length > 0 && (
                    <Paper variant="outlined" sx={{ p: 2, mt: 2, mb: 2 }}>
                      <Typography variant="subtitle1" className="fw_600" gutterBottom>
                        Processed Files:
                      </Typography>
                      <List>
                        {syncResults.fileResults
                          .filter((file) => file.isSuccess)
                          .map((item, index) => (
                            <ListItem
                              key={index}
                              sx={{
                                display: "block",
                                mb: 2,
                                pb: 2,
                                borderBottom:
                                  index < syncResults.fileResults.filter((f) => f.isSuccess).length - 1
                                    ? "1px solid #eee"
                                    : "none",
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                File: {item.fileName}
                              </Typography>
                              <Typography variant="body2" color="success.main">
                                Total Rows: {item.totalRows}
                              </Typography>
                              <Typography variant="body2" color="success.main">
                                Processed Records: {item.processedRows}
                              </Typography>
                              {/* Validation Errors */}
                              {item.validationErrors && item.validationErrors.length > 0 && (
                                <Box sx={{ mt: 1, mb: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Validation Errors:
                                  </Typography>
                                  <List disablePadding sx={{ ml: 2 }}>
                                    {item.validationErrors.map((err, errIndex) => (
                                      <ListItem key={errIndex} sx={{ display: "block", py: 0.5 }}>
                                        <Typography variant="body2" color="warning.main">
                                          • {err.message} {err.body ? `- ${err.body}` : ""}
                                        </Typography>
                                      </ListItem>
                                    ))}
                                  </List>
                                </Box>
                              )}

                              {/* Database Errors */}
                              {item.dbErrors && item.dbErrors.length > 0 && (
                                <Box sx={{ mt: 1, mb: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Database Errors:
                                  </Typography>
                                  <List disablePadding sx={{ ml: 2 }}>
                                    {item.dbErrors.map((err, errIndex) => (
                                      <ListItem key={errIndex} sx={{ display: "block", py: 0.5 }}>
                                        <Typography variant="body2" color="error">
                                          • {err.message} (Affected rows: {err.count})
                                        </Typography>
                                      </ListItem>
                                    ))}
                                  </List>
                                </Box>
                              )}

                              {/* File Errors */}
                              {item.fileErrors && item.fileErrors.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    File Errors:
                                  </Typography>
                                  <Box sx={{ ml: 2 }}>
                                    {item.fileErrors.map((errorLine, lineIndex) => (
                                      <Typography
                                        key={lineIndex}
                                        variant="body2"
                                        color="info.main"
                                        sx={{ mt: lineIndex > 0 ? 0.5 : 0 }}
                                      >
                                        •{" "}
                                        {typeof errorLine === "string"
                                          ? errorLine.split("<br>").map((line, i) => (
                                              <React.Fragment key={i}>
                                                {i > 0 && <br />}
                                                {line.trim()}
                                              </React.Fragment>
                                            ))
                                          : errorLine}
                                      </Typography>
                                    ))}
                                  </Box>
                                </Box>
                              )}

                              {/* No errors case */}
                              {(!item.validationErrors || item.validationErrors.length === 0) &&
                                (!item.dbErrors || item.dbErrors.length === 0) &&
                                (!item.fileErrors || item.fileErrors.length === 0) && (
                                  <Typography variant="body2" color="success.main" sx={{ mt: 1 }}>
                                    All records processed successfully with no errors.
                                  </Typography>
                                )}
                            </ListItem>
                          ))}
                      </List>
                    </Paper>
                  )}

                  {/* Display files with issues */}
                  {syncResults.fileResults && syncResults.fileResults.filter((file) => !file.isSuccess).length > 0 && (
                    <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                      <Typography variant="subtitle1" className="fw_600" gutterBottom>
                        Files with Issues:
                      </Typography>
                      <List>
                        {syncResults.fileResults
                          .filter((file) => !file.isSuccess)
                          .map((item, index) => (
                            <ListItem
                              key={index}
                              sx={{
                                display: "block",
                                mb: 2,
                                pb: 2,
                                borderBottom:
                                  index < syncResults.fileResults.filter((f) => !f.isSuccess).length - 1
                                    ? "1px solid #eee"
                                    : "none",
                              }}
                            >
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                File: {item.fileName}
                              </Typography>
                              <Box sx={{ mt: 0.5 }}>
                                {/* File Errors */}
                                {item.fileErrors && item.fileErrors.length > 0 && (
                                  <Box sx={{ mt: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      File Errors:
                                    </Typography>
                                    <Box sx={{ ml: 2 }}>
                                      {item.fileErrors.map((errorText, errorIndex) => (
                                        <Box key={errorIndex} sx={{ mt: errorIndex > 0 ? 1 : 0 }}>
                                          {typeof errorText === "string" ? (
                                            errorText.split("<br>").map((errorLine, lineIndex) => (
                                              <Typography
                                                key={lineIndex}
                                                variant="body2"
                                                color="info.main"
                                                sx={{
                                                  mt: lineIndex > 0 ? 0.5 : 0,
                                                }}
                                              >
                                                {lineIndex === 0 ? "• " : "  "}
                                                {errorLine.trim()}
                                              </Typography>
                                            ))
                                          ) : (
                                            <Typography variant="body2" color="info.main">
                                              • {String(errorText)}
                                            </Typography>
                                          )}
                                        </Box>
                                      ))}
                                    </Box>
                                  </Box>
                                )}
                              </Box>
                            </ListItem>
                          ))}
                      </List>
                    </Paper>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* SOP Instructions */}
        <Grid>
          <Card className="mt_20">
            <CardContent>
              <Typography variant="h6" className="fw_600" gutterBottom>
                File Format Requirements
              </Typography>

              <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 600 }}>
                For TVHR Files:
              </Typography>
              <ol style={{ paddingLeft: "40px" }}>
                <li>
                  <Typography variant="body1">
                    <strong>Amount</strong> field in the last column will be renamed to <strong>"Amount."</strong>
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    <strong>Vendor Code</strong>, <strong>SBU Code</strong>, <strong>Book Name</strong> will be added
                    with correct value in all the rows otherwise the row will be dropped while uploading.
                  </Typography>
                </li>
              </ol>

              <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 600 }}>
                For TVLR Files:
              </Typography>
              <ol style={{ paddingLeft: "40px" }}>
                <li>
                  <Typography variant="body1">
                    Rows which do not have <strong>SBU Code</strong> will be dropped.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    Rows which do not have <strong>Vendor Code</strong> will be dropped.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    <strong>Book Name</strong> will be taken from the sheet from the row which has{" "}
                    <strong>Book Name</strong> in it.
                  </Typography>
                </li>
                <li>
                  <Typography variant="body1">
                    If <strong>Book Name</strong> not present all rows will be dropped.
                  </Typography>
                </li>
              </ol>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </LoggedInSkeleton>
  );
};

export default LedgerUpload;
